import React, { useEffect, useState } from 'react';
import { Check, X, Ban, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { StepUpDialog } from './StepUpDialog';
import { useAuth } from '../context/AuthContext';

const STATUS = { pending: 'text-yellow-400', approved: 'text-green-400', rejected: 'text-red-400', cancelled: 'text-gray-500', expired: 'text-gray-500' };
const err = (e) => toast.error(e?.response?.data?.detail || 'Operación rechazada');

export const ChangeRequestsPanel = ({ refreshKey }) => {
  const { can, user } = useAuth();
  const [rows, setRows] = useState([]);
  const [dialog, setDialog] = useState(null);

  const load = () => api.get('/admin/infra/change-requests').then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, [refreshKey]);

  const submit = async (x) => {
    try {
      if (dialog.kind === 'approve') await api.post(`/admin/infra/change-requests/${dialog.id}/approve`, { totp_code: x.totp_code, reason: x.reason || null });
      else await api.post(`/admin/infra/change-requests/${dialog.id}/reject`, { reason: x.reason });
      toast.success(dialog.kind === 'approve' ? 'Cambio aprobado y aplicado' : 'Cambio rechazado');
      load();
      return true;
    } catch (e) { err(e); return false; }
  };
  const cancel = (id) => api.post(`/admin/infra/change-requests/${id}/cancel`).then(() => { toast.success('Solicitud cancelada'); load(); }).catch(err);

  return (
    <div className="bg-gray-900 rounded-xl p-6 border border-gray-800 max-w-3xl mt-6" data-testid="change-requests-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-white font-semibold">Solicitudes de cambio</h3>
        <Button variant="ghost" size="sm" onClick={load} data-testid="change-requests-refresh"><RefreshCw className="w-4 h-4" /></Button>
      </div>
      {rows.length === 0 && <p className="text-gray-500 text-sm">No hay solicitudes.</p>}
      <ul className="divide-y divide-gray-800">
        {rows.map((r) => (
          <li key={r.id} className="py-3 text-sm" data-testid={`change-request-${r.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <span className={`font-medium ${STATUS[r.status]}`} data-testid={`change-request-status-${r.id}`}>{r.status}</span>
                <span className="text-gray-400"> · propuesto por {r.proposed_by_email} · {new Date(r.proposed_at).toLocaleString('es-PE')}</span>
              </div>
              {r.status === 'pending' && (
                <div className="flex gap-1">
                  {can('infra.approve') && <>
                    <Button size="sm" className="bg-green-600 hover:bg-green-700" data-testid={`change-approve-${r.id}`}
                      onClick={() => setDialog({ kind: 'approve', id: r.id })}><Check className="w-4 h-4" /></Button>
                    <Button size="sm" className="bg-red-600 hover:bg-red-700" data-testid={`change-reject-${r.id}`}
                      onClick={() => setDialog({ kind: 'reject', id: r.id })}><X className="w-4 h-4" /></Button>
                  </>}
                  {r.proposed_by === user?.id && <Button size="sm" variant="ghost" data-testid={`change-cancel-${r.id}`}
                    onClick={() => cancel(r.id)}><Ban className="w-4 h-4" /></Button>}
                </div>
              )}
            </div>
            <div className="text-xs text-gray-400 mt-1 font-mono">
              {Object.entries(r.payload).map(([k, v]) => <div key={k}>{k}: {String(r.previous?.[k] ?? '—')} → <span className="text-white">{String(v)}</span></div>)}
            </div>
            {r.decided_by_email && <p className="text-xs text-gray-500 mt-1">Decidió {r.decided_by_email}{r.decision_reason ? `: ${r.decision_reason}` : ''}</p>}
          </li>
        ))}
      </ul>
      <StepUpDialog open={!!dialog} title={dialog?.kind === 'approve' ? 'Aprobar y aplicar cambio de infraestructura' : 'Rechazar cambio'}
        needTotp={dialog?.kind === 'approve'} needReason={dialog?.kind === 'reject'} onSubmit={submit} onClose={() => setDialog(null)} />
    </div>
  );
};

export default ChangeRequestsPanel;
